import { useEffect, useRef } from "react";
import { api } from "services/api";

// issue #31: o upload de planilha passou a ser assincrono (job em fila no backend).
// Este hook centraliza o polling de "GET import-jobs/<id>/" para nao duplicar a
// logica entre o upload de logisticMap e o de zzorder, que hoje compartilham o
// mesmo componente (DuelistUploadCard.jsx) mas podiam nao compartilhar no futuro.

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutos: se o worker morrer, nao ficamos presos pra sempre.
// api.js define timeout quase infinito (9999999) para o upload em si; o poll
// precisa do seu proprio timeout curto, senao um GET travado nunca resolve nem
// rejeita, e a deadline de 30min (que so e checada no INICIO de cada tick)
// nunca chega a ser avaliada de novo.
const POLL_REQUEST_TIMEOUT_MS = 15000;

// setTimeout recursivo em vez de setInterval: evita sobrepor requisicoes quando
// o GET demora mais que o intervalo, e nao sofre de stale closure porque cada
// tick agenda o proximo com base no estado corrente (lido via ref), nao numa
// closure fechada no momento do primeiro render.
export const useImportJobPolling = () => {
    const timerIdRef = useRef(null);
    const cancelledRef = useRef(false);
    // Identifica a cadeia de polling atual. Se o usuario reenviar um upload
    // enquanto uma requisicao antiga ainda esta em voo, a resposta tardia dessa
    // cadeia velha precisa ser ignorada mesmo que chegue depois do startPolling novo.
    const pollGenerationRef = useRef(0);

    useEffect(() => {
        return () => {
            // Cobre o caso do componente desmontar com um poll agendado: sem isso o
            // setTimeout dispara depois, tenta fazer setState em componente morto.
            cancelledRef.current = true;
            clearTimeout(timerIdRef.current);
        };
    }, []);

    const stopPolling = () => {
        clearTimeout(timerIdRef.current);
        timerIdRef.current = null;
    };

    const pollOnce = async (jobId, deadline, callbacks, generation) => {
        if (cancelledRef.current || generation !== pollGenerationRef.current) return;
        if (Date.now() > deadline) {
            stopPolling();
            return callbacks.onTimeout();
        }

        try {
            const response = await api.get(`/import-jobs/${jobId}/`, { timeout: POLL_REQUEST_TIMEOUT_MS });
            if (cancelledRef.current || generation !== pollGenerationRef.current) return;
            handleJobStatus(response.data, jobId, deadline, callbacks, generation);
        } catch (error) {
            if (cancelledRef.current || generation !== pollGenerationRef.current) return;
            stopPolling();
            callbacks.onPollError(error);
        }
    };

    const handleJobStatus = (job, jobId, deadline, callbacks, generation) => {
        if (job.status === "success") {
            stopPolling();
            return callbacks.onSuccess(job);
        }
        if (job.status === "failed") {
            stopPolling();
            return callbacks.onFailed(job);
        }
        callbacks.onTick(job);
        timerIdRef.current = setTimeout(() => pollOnce(jobId, deadline, callbacks, generation), POLL_INTERVAL_MS);
    };

    // Inicia o polling de um job recem-enfileirado. `callbacks` precisa de
    // onSuccess, onFailed, onTimeout, onPollError e onTick (status pending/processing).
    const startPolling = (jobId, callbacks) => {
        // Se ja houver um poll rodando (ex: usuario clicou upload de novo), o job
        // anterior fica obsoleto: paramos o timer antigo e incrementamos a geracao,
        // senao uma resposta tardia da cadeia velha ainda conseguiria reagendar
        // outro timer por cima do novo (ver comentario no pollGenerationRef).
        stopPolling();
        cancelledRef.current = false;
        pollGenerationRef.current += 1;
        const deadline = Date.now() + POLL_TIMEOUT_MS;
        pollOnce(jobId, deadline, callbacks, pollGenerationRef.current);
    };

    return { startPolling, stopPolling };
};
